import { BitmapText, Container, Graphics } from "pixi.js";
import { PianoLayout } from "@/lib/piano/piano-layout";
import { PianoComponent } from "@/lib/studio/view/components/piano-component";
import { PlayedNote } from "@/lib/track/lane";
import { NoteGraphic, NoteGraphicProps, NoteStyle, clearNoteContextCache } from "./note";
import { attackLevel } from "./attack";
import { HintSelection } from "@/lib/studio/view/hint-selection";

export type NoteEntry = {
  playedNote: PlayedNote;
  laneIdx: number;
  /**
   * Le tick où la note se dessine. Celui de la note elle-même, sauf pour un
   * morceau hors scène d'une séquence, dont les notes sont décalées de ce
   * qui le sépare du morceau courant — les données ne bougent pas, le
   * dessin si. Tout ce qui place une note sur le rouleau lit celui-ci, et
   * jamais `playedNote.tick`. Voir `SequenceLayout`.
   */
  tick: number;
  /** Pre-computed visual end tick: tickDuration end for finished notes, currentTick for held ones */
  noteEndTick: number;
  /** Fingering: 1 = thumb ... 5 = pinky. */
  finger?: number;
  /** True when the user is currently holding the matching key while the note crosses the playhead. */
  isHit?: boolean;
  /** La note est encore tenue : `noteEndTick` est le tick courant et bouge à chaque image. */
  held: boolean;
};

/**
 * How one lane's notes are painted, resolved upstream from `LaneView`.
 *
 * `outline` is the trace: the live-piano lane drawn as a contour over the
 * written notes rather than as a fill beside them. See `NoteStyle.outline`
 * for the measurements that make it a contour and not a third colour.
 */
export type LaneStyle = {
  readonly fill: number;
  readonly stroke: number;
  readonly outline?: boolean;
  /**
   * La lane sonne-t-elle ? Si oui, chaque note porte sa bande d'attaque
   * (`note.ts`) : toute lane de piano, et toute partie du morceau non coupée.
   */
  readonly dynamics?: boolean;
};

export type NotesProps = {
  entries: NoteEntry[];
  fromTick: number;
  toTick: number;
  width: number;
  height: number;
  hintSelection?: HintSelection;
  /** Per-laneIdx fill/stroke colors (resolved upstream from LaneView). */
  laneColors: readonly LaneStyle[];
  /**
   * Ligne sous laquelle les notes ne sont plus dessinées : la tête de lecture.
   * Ce qu'elle a mangé part en paillettes (`NoteDust`). Jamais les traces, qui
   * sont le relevé du jeu et restent entières.
   */
  clipY?: number;
}

/** Frames between pool shrink checks */
const POOL_SHRINK_INTERVAL = 300; // ~5 seconds at 60 fps
/** Keep this much headroom above the high-water mark when shrinking */
const POOL_SHRINK_HEADROOM = 1.2;

/** Minimum note dimensions (px) to show the finger label. */
const FINGER_MIN_HEIGHT = 14;
const FINGER_MIN_WIDTH = 14;

/** Height range for smooth alpha fade-in (px). */
const FINGER_FADE_MAX = 20;
/**
 * Shortest a trace contour may be drawn (px).
 *
 * A note gets a 2 px floor because at 2 px a filled rectangle is still a
 * legible mark. A contour is not: at 2 px its two horizontal edges meet and
 * it stops reading as an outline at all. Six pixels is where the rim, the
 * hollow and the attack edge are all still there. The floor grows the shape
 * **upward**, away from the attack, so a staccato strike is drawn from the
 * instant it happened and not around it.
 */
const TRACE_MIN_HEIGHT_PX = 6;

/** Finger label rendered at 2x for retina, displayed at 0.5 scale (~11px effective). */
const FINGER_FONT_SIZE = 22;
const FINGER_BASE_SCALE = 0.5;

/** Pill background */
const PILL_COLOR = 0x000000;
const PILL_ALPHA = 0.25;

export class Notes {
  private container: Container;
  private noteLayer: Container;
  private fingerLayer: Container;
  /**
   * Coupe les doigtés à la tête de lecture : une pastille qui passe la ligne
   * y disparaît net, moitié par moitié, comme sa note. Seul ce calque le
   * porte — les notes se coupent elles-mêmes, et les traces restent entières.
   */
  private fingerMask: Graphics;
  private maskWidth = -1;
  private maskBottom = -1;

  // Pool - single array + index (same pattern as Measures)
  private pool: NoteGraphic[] = [];
  private poolIndex = 0;

  // Finger label pool (BitmapText + pill Graphics, paired 1:1)
  private fingerTextPool: BitmapText[] = [];
  private fingerPillPool: Graphics[] = [];
  private fingerPoolIndex = 0;

  // Pool shrink bookkeeping
  private highWaterMark = 0;
  private framesSinceLastShrink = 0;
  private fingerHighWaterMark = 0;

  /**
   * Un style par lane, réutilisé d'une image à l'autre, et un seul objet de
   * propriétés pour toutes les notes : avant, chaque note visible allouait les
   * deux à chaque image — plusieurs milliers d'objets par seconde en lecture,
   * soit précisément le régime qui déclenche les pauses du ramasse-miettes
   * sur tablette. `NoteGraphic.draw` lit tout de suite et ne garde aucune
   * référence, ce qui autorise la mutation en place.
   */
  private readonly styleByLane: NoteStyle[] = [];
  private readonly scratchProps: NoteGraphicProps = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    style: { fillColor: 0, strokeColor: 0, radius: 0, outline: false },
    isBlackKey: false,
    isHit: false,
    held: false,
    cutBottom: false,
    attack: 0,
  };

  constructor(
    private stage: Container,
    private layout: PianoLayout,
    private pianoComponent: PianoComponent,
    private noteGap: number,
    private noteRadius: number,
  ) {
    this.container = new Container();
    this.noteLayer = new Container();
    this.fingerLayer = new Container();
    this.fingerMask = new Graphics();
    this.container.addChild(this.noteLayer);
    this.container.addChild(this.fingerLayer);
    this.container.addChild(this.fingerMask);
    this.fingerLayer.mask = this.fingerMask;
    this.stage.addChild(this.container);
  }

  draw(props: NotesProps): void {
    const { entries, fromTick, toTick, width, height, hintSelection, laneColors, clipY } = props;
    const pxPerTick = height / (toTick - fromTick);
    const hasHighlight = hintSelection?.hasSelection ?? false;
    const maskBottom = clipY ?? height;

    if (width !== this.maskWidth || maskBottom !== this.maskBottom) {
      this.maskWidth = width;
      this.maskBottom = maskBottom;
      this.fingerMask.clear().rect(0, 0, width, Math.max(0, maskBottom)).fill(0xffffff);
    }

    // Cache the shared horizontal projection once per frame instead of 2x per note
    const projection = this.pianoComponent.getProjection();

    this.resetPool();
    this.resetFingerPool();

    for (const entry of entries) {
      const { playedNote, laneIdx, noteEndTick, finger, tick } = entry;

      const laneColor = laneColors[laneIdx] ?? laneColors[laneIdx % Math.max(1, laneColors.length)];
      const outline = laneColor.outline === true;
      const style = this.styleFor(laneIdx);
      style.fillColor = laneColor.fill;
      style.strokeColor = laneColor.stroke;
      style.radius = this.noteRadius;
      style.outline = outline;

      const midi = playedNote.note.toMidi();
      const keyLayout = this.layout.getKeyByMidi(midi);
      if (!keyLayout) continue;

      const x = projection.keyLeft(keyLayout) + this.noteGap;
      const w = projection.keyWidth(keyLayout) - this.noteGap * 2;

      const attackY = height - (tick - fromTick) * pxPerTick;
      const yTop = height - (noteEndTick - fromTick) * pxPerTick;
      const clipped = !outline && clipY !== undefined;

      if (clipped && yTop >= clipY) {
        continue;
      }

      const yBottom = clipped ? Math.min(attackY, clipY) : attackY;
      const cut = clipped && attackY > clipY;
      const noteHeight = Math.max(outline ? TRACE_MIN_HEIGHT_PX : 2, yBottom - yTop - (cut ? 0 : this.noteGap));
      // La note entière, comme si rien ne la coupait : c'est sur elle que le
      // doigté se place. Posé sur la partie visible, il remontait avec la
      // coupe et la note avait l'air de rester au-dessus de la ligne.
      const fullHeight = Math.max(2, attackY - yTop - this.noteGap);
      // A floored contour keeps its bottom edge where the strike was, and
      // grows up. A floored note keeps `yTop`, which is what every other
      // caller of this loop already reads.
      const y = outline ? yBottom - this.noteGap - noteHeight : yTop;

      const noteProps = this.scratchProps;
      noteProps.x = x;
      noteProps.y = y;
      noteProps.width = w;
      noteProps.height = noteHeight;
      noteProps.style = style;
      noteProps.isBlackKey = keyLayout.color === "black";
      noteProps.isHit = entry.isHit ?? false;
      noteProps.held = entry.held;
      noteProps.cutBottom = cut;
      noteProps.attack = laneColor.dynamics === true ? attackLevel(playedNote.velocity) : 0;
      const noteGraphic = this.acquire();
      noteGraphic.draw(noteProps);

      // Dim notes that are not part of the selected hint.
      //
      // Never the trace. The selection is a harmony row of the *score* — the
      // chord under the playhead — and what the player actually struck is not
      // part of it by construction, so every contour would go to 0.15 and
      // vanish exactly on the screen that exists to show them.
      // 0.15 did not put the rest of the passage in the background, it
      // removed it: the practice run selects the current attack alone
      // (`setHintSelection(midis, tick, tick)`), so every other note of the
      // unit — the next one included — was invisible. The step then had to
      // carry its own keyboard in the panel, and the player's eyes went to
      // the panel, on a screen whose whole subject is reading ahead of the
      // playhead. A background, not an eclipse.
      const alpha = hasHighlight && !outline
        ? (hintSelection!.isHighlighted(laneIdx, midi, tick) ? 1.0 : 0.55)
        : 1.0;
      noteGraphic.graphic.alpha = alpha;

      // Show finger number on the note rectangle. Never on a trace: a
      // fingering is an instruction, and the trace is a record.
      // Adaptive Y: near attack point (bottom) for tall notes, centered for short.
      // Measured on the whole note, so the label rides down with it.
      const labelPadding = 8;
      const labelY = fullHeight > 40
        ? yTop + fullHeight - labelPadding - FINGER_FONT_SIZE * FINGER_BASE_SCALE / 2
        : yTop + fullHeight / 2;
      // Passing the line, the label goes under it with its note: the finger
      // layer's mask cuts it there, cleanly. Only a label wholly past the line
      // is skipped.
      const labelGone = clipped && labelY - FINGER_FONT_SIZE * FINGER_BASE_SCALE >= clipY;

      if (!outline && finger != null && fullHeight >= FINGER_MIN_HEIGHT && w >= FINGER_MIN_WIDTH && !labelGone) {
        // Smooth alpha fade between FINGER_MIN_HEIGHT and FINGER_FADE_MAX
        const fingerAlpha = fullHeight < FINGER_FADE_MAX
          ? (fullHeight - FINGER_MIN_HEIGHT) / (FINGER_FADE_MAX - FINGER_MIN_HEIGHT)
          : 1.0;

        // Scale down for narrow notes
        const labelScale = w < 20 ? (w / 20) * FINGER_BASE_SCALE : FINGER_BASE_SCALE;

        const centerX = x + w / 2;
        const combinedAlpha = alpha * fingerAlpha;

        const { pill, text: txt } = this.acquireFinger();

        // Pill (dark circle behind the digit)
        const pillRadius = Math.min(w * 0.4, fullHeight * 0.4, 8) * (labelScale / FINGER_BASE_SCALE);
        pill.clear();
        pill.circle(0, 0, pillRadius);
        pill.fill({ color: PILL_COLOR, alpha: PILL_ALPHA });
        pill.x = centerX;
        pill.y = labelY;
        pill.alpha = combinedAlpha;
        pill.visible = true;

        // Label
        txt.text = String(finger);
        txt.anchor.set(0.5, 0.5);
        txt.x = centerX;
        txt.y = labelY;
        txt.scale.set(labelScale);
        txt.alpha = combinedAlpha;
        txt.visible = true;
      }
    }

    // Hide unused pool entries
    this.hideUnused();
    this.hideUnusedFingers();

    // Track high-water mark and periodically shrink the pools
    if (this.poolIndex > this.highWaterMark) {
      this.highWaterMark = this.poolIndex;
    }
    if (this.fingerPoolIndex > this.fingerHighWaterMark) {
      this.fingerHighWaterMark = this.fingerPoolIndex;
    }
    this.framesSinceLastShrink++;
    if (this.framesSinceLastShrink >= POOL_SHRINK_INTERVAL) {
      this.shrinkPool();
      this.shrinkFingerPool();
      this.framesSinceLastShrink = 0;
    }
  }

  destroy(): void {
    for (const n of this.pool) {
      n.destroy();
    }
    for (let i = 0; i < this.fingerTextPool.length; i++) {
      this.fingerTextPool[i].destroy();
      this.fingerPillPool[i].destroy();
    }

    this.pool.length = 0;
    this.poolIndex = 0;
    this.fingerTextPool.length = 0;
    this.fingerPillPool.length = 0;
    this.fingerPoolIndex = 0;

    // Flush the shared GraphicsContext cache
    clearNoteContextCache();

    this.container.destroy({ children: true });
  }

  private styleFor(laneIdx: number): NoteStyle {
    let style = this.styleByLane[laneIdx];

    if (style === undefined) {
      style = { fillColor: 0, strokeColor: 0, radius: 0, outline: false };
      this.styleByLane[laneIdx] = style;
    }

    return style;
  }

  private resetPool(): void {
    this.poolIndex = 0;
  }

  private acquire(): NoteGraphic {
    if (this.poolIndex < this.pool.length) {
      return this.pool[this.poolIndex++];
    }

    const n = new NoteGraphic();
    this.noteLayer.addChild(n.graphic);
    this.pool.push(n);
    this.poolIndex++;

    return n;
  }

  private hideUnused(): void {
    for (let i = this.poolIndex; i < this.pool.length; i++) {
      this.pool[i].hide();
    }
  }

  /**
   * Shrink the pool to ~120% of the high-water mark observed since the last
   * shrink. This prevents unbounded growth after passing through dense sections.
   */
  private shrinkPool(): void {
    const targetSize = Math.ceil(this.highWaterMark * POOL_SHRINK_HEADROOM);

    if (this.pool.length > targetSize) {
      // Destroy excess Graphics objects
      for (let i = targetSize; i < this.pool.length; i++) {
        this.pool[i].destroy();
      }
      this.pool.length = targetSize;
    }

    // Reset high-water mark for next observation window
    this.highWaterMark = 0;
  }

  // ── Finger label pool (BitmapText + pill Graphics, paired 1:1) ──

  private resetFingerPool(): void {
    this.fingerPoolIndex = 0;
  }

  /**
   * Acquire a paired (pill, text) at the current fingerPoolIndex.
   * Both arrays grow in lockstep so indices always match.
   */
  private acquireFinger(): { pill: Graphics; text: BitmapText } {
    const idx = this.fingerPoolIndex++;

    if (idx < this.fingerTextPool.length) {
      return { pill: this.fingerPillPool[idx], text: this.fingerTextPool[idx] };
    }

    // Grow both pools together
    const pill = new Graphics();
    this.fingerLayer.addChild(pill);
    this.fingerPillPool.push(pill);

    const text = new BitmapText({
      text: '',
      style: {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: FINGER_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
      },
    });
    text.scale.set(FINGER_BASE_SCALE);
    this.fingerLayer.addChild(text);
    this.fingerTextPool.push(text);

    return { pill, text };
  }

  private hideUnusedFingers(): void {
    for (let i = this.fingerPoolIndex; i < this.fingerTextPool.length; i++) {
      this.fingerTextPool[i].visible = false;
      this.fingerPillPool[i].visible = false;
    }
  }

  private shrinkFingerPool(): void {
    const targetSize = Math.ceil(this.fingerHighWaterMark * POOL_SHRINK_HEADROOM);

    if (this.fingerTextPool.length > targetSize) {
      for (let i = targetSize; i < this.fingerTextPool.length; i++) {
        this.fingerTextPool[i].destroy();
        this.fingerPillPool[i].destroy();
      }
      this.fingerTextPool.length = targetSize;
      this.fingerPillPool.length = targetSize;
    }

    this.fingerHighWaterMark = 0;
  }
}
